import { InfoOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import type React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { formatDate } from "../../../utils/dateUtils";
import { verificationReason } from "../utils/statusDetail";
import UpdateInsuranceCompanyModal from "./UpdateInsuranceCompanyModal";
import UpdateProductTypeModal from "./UpdateProductTypeModal";

const extractBennyErrorMessage = (reason: string): string => {
  const base = reason.startsWith("Message: ")
    ? reason.slice("Message: ".length)
    : reason;
  const cutoffs = [
    "Date of Service",
    "Transaction ID",
    "Params:",
    ". verification:",
    ", Params:",
  ];
  const msg = cutoffs.reduce((acc, cutoff) => {
    const idx = acc.indexOf(cutoff);
    return idx !== -1 ? acc.slice(0, idx) : acc;
  }, base);
  return msg.trim();
};

const getLedgerEntryColor = (ledgerEntry: string) => {
  if (!ledgerEntry)
    return { bg: "grey.50", border: "grey.200", text: "text.primary" };

  // Red for no coverage cases
  if (
    ledgerEntry.includes("No Ortho Coverage") ||
    ledgerEntry.includes("No Active Ortho Coverage")
  ) {
    return { bg: "#ffebee", border: "#ffcdd2", text: "#d32f2f" };
  }

  // Green for active coverage with benefits (contains lifetime max, percentages, etc.)
  if (
    ledgerEntry.includes("Lifetime max") ||
    ledgerEntry.includes("Plan pays")
  ) {
    return { bg: "#e8f5e8", border: "#c8e6c9", text: "#2e7d32" };
  }

  // Yellow for any other cases (intermediary)
  return { bg: "#fff3e0", border: "#ffcc02", text: "#ef6c00" };
};

const formatAddress = (locationInfo: LocationInfo | null | undefined) => {
  if (!locationInfo) return null;

  const parts = [
    locationInfo.streetAddress,
    locationInfo.city,
    locationInfo.state,
    locationInfo.postalCode,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : null;
};

const getSubscriberName = (verification: InsuranceVerificationMessage) => {
  const {
    subscriberName,
    subscriberFirstName,
    subscriberMiddleName,
    subscriberLastName,
  } = verification;

  if (subscriberName) {
    return subscriberName;
  }

  if (subscriberFirstName && subscriberMiddleName && subscriberLastName) {
    return `${subscriberFirstName} ${subscriberMiddleName} ${subscriberLastName}`;
  }

  if (subscriberFirstName && subscriberLastName) {
    return `${subscriberFirstName} ${subscriberLastName}`;
  }

  return null;
};

interface ContactInformationCardProps {
  verification: InsuranceVerificationMessage;
  supportedPayers: CredentialsSupportedPayersMessage[];
  onPayerMapped: (payerId: string, payerName: string) => Promise<void>;
  onProductTypeUpdated: (newProductType: string) => Promise<void>;
}

function InfoRow({
  label,
  value,
  fallback,
}: {
  label: string;
  value?: string | null;
  fallback?: string;
}) {
  const displayValue = value || fallback;
  const hasValue = Boolean(value);

  return (
    <Box sx={{ display: "flex", mb: 1 }}>
      <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 600 }}>
        {label}:
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: hasValue ? "text.primary" : "text.secondary",
          fontStyle: hasValue ? "normal" : "italic",
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
}

function PatientInformationSection({
  verification,
}: {
  verification: InsuranceVerificationMessage;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Patient Information
      </Typography>
      <Box sx={{ pl: 2 }}>
        <Box sx={{ display: "flex", mb: 1 }}>
          <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 600 }}>
            Name:
          </Typography>
          {verification.patientKey ? (
            <Link
              to={`/patients/${verification.patientKey}`}
              style={{
                textDecoration: "underline",
                color: "inherit",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  textDecoration: "underline",
                }}
              >
                {verification.patientName}
              </Typography>
            </Link>
          ) : (
            <Typography variant="body2">{verification.patientName}</Typography>
          )}
        </Box>
        <InfoRow
          label="DOB"
          value={
            verification.patientDob
              ? formatDate(verification.patientDob)
              : "Not provided"
          }
        />
        <InfoRow
          label="Relationship"
          value={verification.wieldyRelationshipToSubscriber}
          fallback="-"
        />
        <InfoRow
          label="Zip Code"
          value={verification.patientZipcode}
          fallback="-"
        />
      </Box>
    </Box>
  );
}

function showProductType(
  productType: string | null,
  productTypeV2Enabled: boolean,
  openUpdateModal: () => void,
): React.ReactElement | null {
  if (!productTypeV2Enabled) {
    return null;
  }
  return (
    <Box
      sx={{
        display: "flex",
        mb: 1,
        alignItems: "center",
        cursor: "pointer",
        "&:hover": { opacity: 0.8 },
      }}
      onClick={() => openUpdateModal()}
    >
      <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 600 }}>
        Product Type:
      </Typography>
      {productType ? (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.25,
            borderRadius: 1,
            border: "1px solid #1976d2",
            backgroundColor: "#e3f2fd",
          }}
        >
          <Typography variant="body2">{productType}</Typography>
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontStyle: "italic" }}
        >
          Not set
        </Typography>
      )}
    </Box>
  );
}

function InsuranceCompanySection({
  verification,
  supportedPayers,
  onPayerMapped,
  onProductTypeUpdated,
}: {
  verification: InsuranceVerificationMessage;
  supportedPayers: CredentialsSupportedPayersMessage[];
  onPayerMapped: (payerId: string, payerName: string) => Promise<void>;
  onProductTypeUpdated: (newProductType: string) => Promise<void>;
}) {
  const networkStatus = verification.locationInfo?.networkStatus;
  const networkTier = verification.locationInfo?.networkTier;
  const [tagPayerModalOpen, setTagPayerModalOpen] = useState(false);
  const [payerSaving, setPayerSaving] = useState(false);
  const [updateProductTypeModalOpen, setUpdateProductTypeModalOpen] =
    useState(false);
  const [productTypeSaving, setProductTypeSaving] = useState(false);

  const isNormalized = Boolean(verification.payerId);

  const getNetworkStatusDisplay = (
    status: NetworkStatus | null | undefined,
  ) => {
    let displayValue: string;
    let color: string;
    let fontWeight: number;
    let fontStyle: string;

    switch (status) {
      case "IN_NETWORK":
        displayValue = "In Network";
        color = "#2e7d32";
        fontWeight = 600;
        fontStyle = "normal";
        break;
      case "OUT_OF_NETWORK":
        displayValue = "Out of Network";
        color = "#d32f2f";
        fontWeight = 600;
        fontStyle = "normal";
        break;
      default:
        displayValue = "Unavailable";
        color = "#757575";
        fontWeight = 400;
        fontStyle = "italic";
    }

    return { displayValue, color, fontWeight, fontStyle };
  };

  const networkStatusDisplay = getNetworkStatusDisplay(networkStatus);

  const handlePayerSave = async (payerId: string, payerName: string) => {
    setPayerSaving(true);
    try {
      await onPayerMapped(payerId, payerName);
      setTagPayerModalOpen(false);
    } finally {
      setPayerSaving(false);
    }
  };

  const handleProductTypeSave = async (newProductType: string) => {
    setProductTypeSaving(true);
    try {
      await onProductTypeUpdated(newProductType);
      setUpdateProductTypeModalOpen(false);
    } finally {
      setProductTypeSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Insurance Company
      </Typography>
      <Box sx={{ pl: 2 }}>
        <Box
          data-testid="insuranceCompanyField"
          sx={{
            display: "flex",
            mb: 1,
            alignItems: "center",
            cursor: "pointer",
            "&:hover": { opacity: 0.8 },
          }}
          onClick={() => setTagPayerModalOpen(true)}
        >
          <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 600 }}>
            Company:
          </Typography>
          <Tooltip
            title={
              isNormalized
                ? `PMS Insurance Name: ${verification.insuranceCompany}`
                : "No Wieldy Payer Name tagged. Click to tag."
            }
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                border: "1px solid",
                borderColor: isNormalized ? "#4caf50" : "#ff9800",
                backgroundColor: isNormalized ? "#f1f8e9" : "#fff8e1",
              }}
            >
              {isNormalized ? (
                <InfoOutlined sx={{ fontSize: 14, color: "#4caf50" }} />
              ) : (
                <WarningAmberOutlined sx={{ fontSize: 14, color: "#ff9800" }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: (
                    isNormalized
                      ? verification.payerName
                      : verification.insuranceCompany
                  )
                    ? "text.primary"
                    : "text.secondary",
                  fontStyle: (
                    isNormalized
                      ? verification.payerName
                      : verification.insuranceCompany
                  )
                    ? "normal"
                    : "italic",
                }}
              >
                {isNormalized
                  ? verification.payerName || "Not provided"
                  : verification.insuranceCompany || "Not provided"}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        <InfoRow
          label="Phone"
          value={verification.insurancePhoneNumber}
          fallback="Look up required"
        />
        {showProductType(
          verification.wieldyProductType,
          verification.productTypeV2Enabled,
          () => setUpdateProductTypeModalOpen(true),
        )}
        <Box sx={{ display: "flex", mb: 1 }}>
          <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 600 }}>
            Network Status:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: networkStatusDisplay.color,
              fontWeight: networkStatusDisplay.fontWeight,
              fontStyle: networkStatusDisplay.fontStyle,
            }}
          >
            {networkStatusDisplay.displayValue}
            {networkTier && (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 400,
                  fontStyle: "normal",
                  ml: 0.5,
                }}
              >
                ({networkTier})
              </Typography>
            )}
          </Typography>
        </Box>
      </Box>
      <UpdateInsuranceCompanyModal
        open={tagPayerModalOpen}
        onClose={() => setTagPayerModalOpen(false)}
        onSave={handlePayerSave}
        supportedPayers={supportedPayers}
        currentCompanyName={verification.insuranceCompany}
        currentPayerName={verification.payerName}
        saving={payerSaving}
      />
      <UpdateProductTypeModal
        open={updateProductTypeModalOpen}
        onClose={() => setUpdateProductTypeModalOpen(false)}
        onSave={handleProductTypeSave}
        currentProductType={verification.wieldyProductType}
        saving={productTypeSaving}
      />
    </Box>
  );
}

function SubscriberInformationSection({
  verification,
}: {
  verification: InsuranceVerificationMessage;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Subscriber Information
      </Typography>
      <Box sx={{ pl: 2 }}>
        <InfoRow
          label="Name"
          value={getSubscriberName(verification)}
          fallback="Not provided"
        />
        <InfoRow
          label="DOB"
          value={
            verification.subscriberDob
              ? formatDate(verification.subscriberDob)
              : null
          }
          fallback="Not provided"
        />
        <InfoRow
          label="Policy #"
          value={verification.policyNumber}
          fallback="Not provided"
        />
        <InfoRow
          label="Group #"
          value={verification.groupNumber}
          fallback="Not provided"
        />
      </Box>
    </Box>
  );
}

function LocationInformationSection({
  verification,
}: {
  verification: InsuranceVerificationMessage;
}) {
  const { locationInfo, wieldyPolicyNotes } = verification;
  const verificationStatusReason = verificationReason(verification);

  return (
    <Box sx={{ pl: 2 }}>
      <InfoRow
        label="Practice Name"
        value={locationInfo?.practiceDisplayName}
        fallback="Unavailable"
      />
      <InfoRow
        label="Location Name"
        value={locationInfo?.name}
        fallback="Unavailable"
      />
      <InfoRow
        label="Address"
        value={formatAddress(locationInfo)}
        fallback="Unavailable"
      />
      <InfoRow
        label="Phone"
        value={locationInfo?.phoneNumber}
        fallback="Unavailable"
      />
      <InfoRow
        label="Tax ID"
        value={locationInfo?.taxId}
        fallback="Unavailable"
      />
      <InfoRow
        label="Provider"
        value={locationInfo?.providerName}
        fallback="Unavailable"
      />
      <InfoRow
        label="NPI"
        value={locationInfo?.providerNpi}
        fallback="Unavailable"
      />
      {wieldyPolicyNotes && (
        <>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, fontSize: "0.875rem" }}
            >
              Policy Notes
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              backgroundColor: getLedgerEntryColor(wieldyPolicyNotes).bg,
              borderRadius: 1,
              border: "1px solid",
              borderColor: getLedgerEntryColor(wieldyPolicyNotes).border,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                color: getLedgerEntryColor(wieldyPolicyNotes).text,
                fontWeight: 500,
              }}
            >
              {wieldyPolicyNotes}
            </Typography>
          </Box>
        </>
      )}
      {verificationStatusReason && (
        <>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, fontSize: "0.875rem" }}
            >
              Benny Notes
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#fff8e1",
              borderRadius: 1,
              border: "1px solid #ffe082",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#e65100", fontWeight: 500 }}
            >
              {extractBennyErrorMessage(verificationStatusReason)}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

export default function ContactInformationCard({
  verification,
  supportedPayers,
  onPayerMapped,
  onProductTypeUpdated,
}: ContactInformationCardProps) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
        >
          Patient & Insurance Information
        </Typography>

        <PatientInformationSection verification={verification} />
        <InsuranceCompanySection
          verification={verification}
          supportedPayers={supportedPayers}
          onPayerMapped={onPayerMapped}
          onProductTypeUpdated={onProductTypeUpdated}
        />
        <SubscriberInformationSection verification={verification} />
      </Box>

      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
        >
          Location Information
        </Typography>

        <LocationInformationSection verification={verification} />
      </Box>
    </Box>
  );
}
